import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { Alert, Button, CircularProgress, Divider, IconButton, Stack } from "@mui/material";
import { HeaderMenuButtonRow } from "@src/appV2/redesign/components/HeaderMenuButton/HeaderMenuButtonRow";
import { Text } from "@src/appV2/redesign/components/Text";
import { Title } from "@src/appV2/redesign/components/Title";
import { useHistory } from "react-router-dom";

import {
  getHomeHealthAddVisitPath,
  getHomeHealthEditCasePath,
  HOME_HEALTH_CASES_PATH,
} from "../paths";
import { type Case, CaseStatus } from "../types";
import { MobileVisitCard } from "./MobileVisitCard";

const addVisitButtonSx = {
  borderRadius: "24px",
  py: 1.5,
  textTransform: "none",
  fontSize: "1rem",
} as const;

interface Props {
  caseId: string;
  case: Case | undefined;
  isLoading: boolean;
  isError: boolean;
}

export function MobileCaseDetailView({ caseId, case: caseItem, isLoading, isError }: Props) {
  const history = useHistory();

  if (isLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ minHeight: "40vh" }}>
        <CircularProgress />
      </Stack>
    );
  }

  if (isError || !caseItem) {
    return (
      <Alert severity="error" sx={{ m: 3 }}>
        Error loading case.
      </Alert>
    );
  }

  const { patient, visits, status } = caseItem.attributes;
  const isCaseEditable = status === CaseStatus.OPEN;

  return (
    <Stack
      sx={(theme) => ({
        height: "100%",
        minHeight: "100vh",
        bgcolor: "background.baseDark",
        padding: theme.spacing(3),
      })}
    >
      <Stack
        sx={(theme) => ({
          paddingTop: theme.spacing(2),
          paddingX: theme.spacing(3),
          gap: theme.spacing(1),
        })}
      >
        <Stack direction="row" justifyContent="flex-end">
          {isCaseEditable && (
            <IconButton
              aria-label="Edit case"
              onClick={() => {
                history.push(getHomeHealthEditCasePath(caseId));
              }}
            >
              <EditOutlinedIcon />
            </IconButton>
          )}
          <IconButton
            aria-label="Go back"
            onClick={() => {
              if (history.length > 1) {
                history.goBack();
              } else {
                history.replace(HOME_HEALTH_CASES_PATH);
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
        <HeaderMenuButtonRow>
          <Title component="h5" data-dd-privacy="mask">
            {patient.attributes.externalPatientIdentifier}
          </Title>
        </HeaderMenuButtonRow>
        <Stack direction="row" alignItems="flex-start" spacing={0.5} sx={{ mb: 2 }}>
          <LocationOnIcon sx={{ fontSize: 18, mt: 0.2, color: "text.secondary", flexShrink: 0 }} />
          <Text variant="body1" data-dd-privacy="mask" sx={{ color: "text.secondary" }}>
            {patient.attributes.formattedAddress}
          </Text>
        </Stack>
        <Divider />
      </Stack>
      <Stack
        sx={(theme) => ({
          flex: 1,
          overflowY: "auto",
          paddingTop: theme.spacing(4),
          paddingX: theme.spacing(3),
          paddingBottom: theme.spacing(4),
          gap: theme.spacing(5),
        })}
      >
        <Button
          fullWidth
          variant="primary"
          startIcon={<AddIcon />}
          onClick={() => {
            history.push(getHomeHealthAddVisitPath(caseId));
          }}
          sx={addVisitButtonSx}
        >
          Add Visit
        </Button>
        {visits.length === 0 ? (
          <Stack alignItems="center" justifyContent="center" sx={{ minHeight: "40vh" }}>
            <Text variant="body1" sx={(theme) => ({ color: theme.palette.text.disabled })}>
              No visits yet
            </Text>
          </Stack>
        ) : (
          visits.map((visit) => <MobileVisitCard key={visit.id} visit={visit} />)
        )}
      </Stack>
    </Stack>
  );
}
